import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ResponsiveContainer, LineChart, Line, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { GraduationCap, ArrowUpRight, AlertCircle, AlertTriangle, CalendarX, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { STATUS_COLORS, formatDate } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AttentionChip = ({ icon: Icon, label, tone = 'amber', testId }) => {
  const cls = {
    amber: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700',
    red: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700',
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium border px-2 py-0.5 rounded-full ${cls}`} data-testid={testId}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

const TrendBadge = ({ direction, delta }) => {
  if (!direction) return null;
  const map = {
    improving: { Icon: TrendingUp, cls: 'text-emerald-700 dark:text-emerald-300', label: `Improving${delta != null ? ` (+${delta}%)` : ''}` },
    declining: { Icon: TrendingDown, cls: 'text-red-700 dark:text-red-300', label: `Declining${delta != null ? ` (${delta}%)` : ''}` },
    stable:    { Icon: Minus,        cls: 'text-gray-500 dark:text-gray-400', label: `Stable${delta != null ? ` (${delta >= 0 ? '+' : ''}${delta}%)` : ''}` },
  }[direction];
  if (!map) return null;
  const { Icon, cls, label } = map;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] ${cls}`} data-testid="athlete-360-trend-badge">
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

const MiniTrend = ({ trend }) => {
  if (!trend || !trend.points || trend.points.length === 0) {
    return (
      <p className="text-[10px] text-gray-500 dark:text-gray-400" data-testid="athlete-360-trend-empty">No academic attendance yet.</p>
    );
  }
  const isSingle = trend.points.length < 2;
  return (
    <div data-testid="athlete-360-attendance-trend" className="border border-gray-200 dark:border-gray-700 rounded-md p-2 bg-gray-50 dark:bg-gray-900/30">
      <div className="flex justify-between items-baseline">
        <div>
          <p className="text-[11px] font-medium text-gray-700 dark:text-gray-200">Academic Attendance Trend</p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Avg {trend.average_rate}% · {trend.points.length} week{trend.points.length === 1 ? '' : 's'}</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300" data-testid="athlete-360-trend-latest">{trend.latest_rate}%</p>
          <TrendBadge direction={trend.trend_direction} delta={trend.trend_delta} />
        </div>
      </div>
      {isSingle ? (
        <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center py-1" data-testid="athlete-360-trend-not-enough">Not enough history.</p>
      ) : (
        <div style={{ width: '100%', height: 48 }} className="mt-1">
          <ResponsiveContainer>
            <LineChart data={trend.points} margin={{ top: 2, right: 2, left: 0, bottom: 0 }}>
              <Tooltip
                cursor={false}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-[10px] shadow-sm">
                      <div className="font-medium">{p.week_start_date}</div>
                      <div className="text-emerald-700 dark:text-emerald-300">Rate: {p.rate}%</div>
                    </div>
                  );
                }}
              />
              <Line type="monotone" dataKey="rate" stroke="#059669" strokeWidth={2} dot={{ r: 1.5, fill: '#059669' }} activeDot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const Athlete360AcademicSummary = ({ athleteId }) => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!athleteId) return;
    let alive = true;
    axios.get(`${API}/academic/summary/${athleteId}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [athleteId]);

  if (!data) return null;
  const empty = !data.profile_count && !data.latest_report && !data.latest_attendance && !data.latest_document;
  const att = data.attention || {};

  return (
    <Card data-testid="athlete-360-academic-summary">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-base flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-emerald-600" /> Academic
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={() => navigate(`/athletes/${athleteId}/academic`)} data-testid="athlete-360-open-academic">
            Open <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {empty ? (
          <p className="text-sm text-gray-500">No academic profile added yet.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">Profiles: {data.profile_count}</span>
              {(data.profiles || []).slice(0, 4).map(p => (
                <Badge key={p.id} className={`text-[10px] ${STATUS_COLORS[p.status] || STATUS_COLORS.Good}`}>
                  {p.name}{p.status ? ` · ${p.status}` : ''}
                </Badge>
              ))}
            </div>
            {(att.has_attention_status || att.recent_absent_count > 0 || att.low_grade) && (
              <div className="flex flex-wrap gap-1.5" data-testid="athlete-360-academic-attention">
                {att.has_attention_status && <AttentionChip icon={AlertCircle} label="Profile flagged" tone="amber" testId="athlete-att-status" />}
                {att.recent_absent_count > 0 && (
                  <AttentionChip icon={CalendarX} label={`Recent Absent/No-Show · ${att.recent_absent_count}`} tone="amber" testId="athlete-att-absent" />
                )}
                {att.low_grade && (
                  <AttentionChip icon={TrendingDown} label={`Low avg grade · ${att.latest_avg_grade_numeric}`} tone="red" testId="athlete-att-low-grade" />
                )}
                {att.latest_weekly_rate !== null && att.latest_weekly_rate !== undefined && att.latest_weekly_rate < 80 && (
                  <AttentionChip icon={AlertTriangle} label={`Weekly rate · ${att.latest_weekly_rate}%`} tone="amber" testId="athlete-att-weekly-rate" />
                )}
              </div>
            )}
            {/* Phase 72.E — compact academic attendance mini-trend. */}
            <MiniTrend trend={data.academic_attendance_trend} />
            {data.latest_report && (
              <p className="text-xs text-gray-600 dark:text-gray-300">
                Latest report: <strong>{data.latest_report.period}</strong>{data.latest_report.average_grade ? ` · ${data.latest_report.average_grade}` : ''}{' · '}{formatDate(data.latest_report.created_at)}
              </p>
            )}
            {data.latest_attendance && (
              <div className="text-xs text-gray-600 dark:text-gray-300">
                This week: <Badge className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-0">{data.latest_attendance.status}</Badge>
                {data.latest_attendance.rate !== null && data.latest_attendance.rate !== undefined && (
                  <> · {data.latest_attendance.rate}%</>
                )}
                {' '}({formatDate(data.latest_attendance.week_start_date)})
              </div>
            )}
            {data.latest_document && (
              <p className="text-xs text-gray-600 dark:text-gray-300">
                Latest document: <strong>{data.latest_document.document_type}</strong>{data.latest_document.title ? ` — ${data.latest_document.title}` : ''}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default Athlete360AcademicSummary;
